import React, { useMemo } from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import Cookies from "js-cookie";
import { getSessionToken } from "@shopify/app-bridge-utils";
import { useAppBridge } from "./Components/Shopify/ShopifyAppBridgeProvider";

interface ApolloClientProviderProps {
  children: React.ReactNode;
}

/**
 * Apollo Client that supports BOTH:
 * - Shopify session tokens (embedded app)
 * - Cookie-based auth (existing users)
 */
const ApolloClientProvider: React.FC<ApolloClientProviderProps> = ({
  children,
}) => {
  const app = useAppBridge();

  // GraphQL endpoint
  const httpLink = useMemo(
    () =>
      createHttpLink({
        uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
      }),
    []
  );

  // Auth link (handles both Shopify + cookie tokens)
  const authLink = setContext(async (_, { headers }) => {
    let token = Cookies.get("authToken"); // default: use your cookie token
    const shopDomain = Cookies.get("shopDomain") || null;
    let shopifyToken = null;

    if (app) {
      try {
        // Try to fetch Shopify session token (if in embedded app)
        shopifyToken = await getSessionToken(app);
      } catch (error) {
        console.warn("Failed to fetch Shopify session token:", error);
      }
    }

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
        ...(shopifyToken ? { "X-Shopify-Access-Token": shopifyToken } : {}),
        ...(shopDomain ? { "X-Shop-Domain": shopDomain } : {}),
      },
    };
  });

  // Error handling stays the same
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        switch (err.extensions?.code) {
          case "UNAUTHENTICATED":
            Cookies.remove("authToken", {
              secure: true,
              sameSite: "Strict",
            });
            window.location.href = "/login";
            break;
          default:
            console.error("[GraphQL Error]", err.message);
        }
      }
    }
    if (networkError) {
      console.error("[Network Error]:", networkError);
    }
  });

  // Apollo client setup
  const client = useMemo(
    () =>
      new ApolloClient({
        link: from([errorLink, authLink.concat(httpLink)]),
        cache: new InMemoryCache(),
      }),
    [app]
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloClientProvider;
